import type { Lootbox, PlayerBonus, PlayerFreespin } from 'api/common';
import { BonusStage, LootboxStage } from 'api/common';
import { INSTANT_REWARD_KEYS, SCHEDULER_GROUP_PATTERN } from '../constants';

export type TReward = PlayerBonus | PlayerFreespin | Lootbox;

/** level order -> lootbox to open, or null when the reward is a plain bonus */
export type TIssuedRewards = ReadonlyMap<number, number | null>;

const getInstantOrder = ({ groupKey }: TReward): number | null =>
  (groupKey && INSTANT_REWARD_KEYS[groupKey.toLowerCase()]) || null;

// pumpkin_rush_scheduler_l3 -> 3
const getWeeklyOrder = ({ groupKey }: TReward): number | null => {
  const match = groupKey
    ? SCHEDULER_GROUP_PATTERN.exec(groupKey.toLowerCase())
    : null;

  return match ? Number.parseInt(match[1], 10) : null;
};

const isWaiting = ({ stage }: TReward): boolean =>
  stage === BonusStage.Issued || stage === LootboxStage.Issued;

const isLootbox = (reward: TReward): reward is Lootbox => 'items' in reward;

// the same bonuses come back every week, only this week's ones count
const isCreatedSince = ({ createdAt }: TReward, since: Date): boolean =>
  new Date(createdAt) >= since;

/**
 * Levels whose reward has been issued this week and is still waiting for the
 * player. Several of them can pile up, since clearing a level does not activate
 * the bonus, and L4 arrives as a lootbox that has to be opened.
 */
export const getIssuedRewards = (
  rewards: TReward[],
  cycleStart: Date
): TIssuedRewards =>
  rewards.reduce<Map<number, number | null>>((issued, reward) => {
    const order = getInstantOrder(reward);

    if (
      order !== null &&
      isWaiting(reward) &&
      isCreatedSince(reward, cycleStart)
    ) {
      issued.set(order, isLootbox(reward) ? reward.id : null);
    }

    return issued;
  }, new Map());

export interface IWeeklyRewards {
  /** order of a weekly reward that still waits to be activated */
  waitingOrder: number | null;
  /** order of the weekly reward credited since the given moment */
  creditedOrder: number | null;
}

const maxOrder = (current: number | null, order: number): number =>
  Math.max(current ?? 0, order);

/**
 * A waiting reward counts whenever it was credited: it lives a day, and the
 * issue can land a little off the turn of the week.
 */
export const getWeeklyRewards = (
  rewards: TReward[],
  since: Date
): IWeeklyRewards =>
  rewards.reduce<IWeeklyRewards>(
    (found, reward) => {
      const order = getWeeklyOrder(reward);

      if (order === null) return found;

      return {
        waitingOrder: isWaiting(reward)
          ? maxOrder(found.waitingOrder, order)
          : found.waitingOrder,
        creditedOrder: isCreatedSince(reward, since)
          ? maxOrder(found.creditedOrder, order)
          : found.creditedOrder,
      };
    },
    { waitingOrder: null, creditedOrder: null }
  );
